import type { Class } from "../types";
import { brand } from "../brand";
import { ALL_PRODUCTS } from "../pages/SignUpPage/products";

function productIdForClassName(className: string): string | undefined {
  const normalized = className.trim().toLowerCase();
  const match = ALL_PRODUCTS.find((product) => product.title.toLowerCase() === normalized);
  if (match) return match.id;
  // Legacy class name still present for some MRCGP AKT enrolments
  if (normalized === "mrcgp") return "mrcgp_akt";
  return undefined;
}

/**
 * Stable display order for enrolments / classes (sidebar + dashboard cards).
 * Prefer the brand catalogue order (productIds), then name, then classId.
 */
export function sortEnrolments<T extends Pick<Class, "classId" | "className">>(
  enrolments: readonly T[]
): T[] {
  const productOrder = new Map(
    (brand.productIds as readonly string[]).map((id, index) => [id, index])
  );

  const orderIndex = (className: string): number => {
    const productId = productIdForClassName(className);
    if (productId !== undefined && productOrder.has(productId)) {
      return productOrder.get(productId)!;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  return [...enrolments].sort((a, b) => {
    const orderDiff = orderIndex(a.className) - orderIndex(b.className);
    if (orderDiff !== 0) return orderDiff;

    const nameDiff = a.className.localeCompare(b.className, undefined, {
      sensitivity: "base",
    });
    if (nameDiff !== 0) return nameDiff;

    return a.classId - b.classId;
  });
}
